import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useContext } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Fade,
  Skeleton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Chip,
  TextField,
  useMediaQuery,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import InboxIcon from "@mui/icons-material/Inbox";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";

import { useListViewMode } from "../hooks/useListPreferences";
import { GlobalSearchBar } from "../Components/ERPList";
import CreateAppointmentWizard from "../Components/CreateAppointmentWizard";
import { appointmentsApi } from "../Components/appointmentsApi";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { url } from "../Components/url";
import {
  appointmentStatusMeta,
  formatDateTime,
  statusLabel,
} from "../Components/appointmentsUiUtils";
import { fetchAppointments, fetchAppointmentTypes } from "../store/appointmentsSlice";
import { UserContext } from "../Contexts/LoginContext";
import listStyles from "./AllEnquiries.module.scss";
import { useTheme } from "../hooks/useTheme";
import { createMuiThemeFromMode } from "../theme/theme";

const DEFAULT_LIST_ID = "my-appointments";
const PAGE_SIZE_OPTIONS = [10, 12, 25, 50, 100];

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: { styleOverrides: { root: { boxShadow: "none" } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
};

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[10],
  },
}));

function StatusChip({ status }) {
  const meta = appointmentStatusMeta[status] || appointmentStatusMeta.booked;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={statusLabel(status)}
      sx={{
        borderColor: meta.color,
        color: meta.color,
        bgcolor: `${meta.color}18`,
        fontWeight: 600,
      }}
    />
  );
}

const COUNSELLOR_ROLE_NAMES = ["Counsellor", "Visa Counsellor", "Visa&Application Counsellor"];

function parseUserRoles(roleStr) {
  return (roleStr || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildAppointmentListParams(filters, page, perPage, counsellorId) {
  const out = { page, per_page: perPage };
  if ((filters.search || "").trim()) out.search = filters.search.trim();
  if (filters.status) out.status = filters.status;
  if (filters.appointment_type_id) {
    const tid = Number(filters.appointment_type_id);
    if (!Number.isNaN(tid)) out.appointment_type_id = tid;
  }
  if ((filters.customer_email || "").trim()) out.customer_email = filters.customer_email.trim();
  if (filters.start_date) out.start_date = filters.start_date;
  if (filters.end_date) out.end_date = filters.end_date;
  if (counsellorId != null && !Number.isNaN(Number(counsellorId))) {
    out.counsellor_id = Number(counsellorId);
  }
  return out;
}

export default function AppointmentsMyPage({
  showAllAppointments = false,
  pageTitle = "My Appointments",
  pageSubtitle = "Manage bookings, statuses, and customer schedules.",
  listId = DEFAULT_LIST_ID,
}) {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, types, loading, pagination, error } = useSelector((state) => state.appointments);
  const { username } = useContext(UserContext);

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    counsellor_id: "",
    appointment_type_id: "",
    customer_email: "",
    start_date: "",
    end_date: "",
  });
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [counsellors, setCounsellors] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useListViewMode(listId, "table");
  const toggleViewMode = () => setViewMode((prev) => (prev === "cards" ? "table" : "cards"));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [filterOpen, setFilterOpen] = useState(!isMobile);

  const displayTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;

  const counsellorNameById = useMemo(() => {
    const map = new Map();
    allUsers.forEach((item) => {
      map.set(String(item.id), item.username);
    });
    return map;
  }, [allUsers]);

  const myCounsellorId = useMemo(() => {
    const u = allUsers.find((x) => String(x.username) === String(username));
    return u?.id != null ? Number(u.id) : undefined;
  }, [allUsers, username]);

  useEffect(() => {
    dispatch(fetchAppointmentTypes());
  }, [dispatch]);

  useEffect(() => {
    const cid = showAllAppointments
      ? filters.counsellor_id
        ? Number(filters.counsellor_id)
        : undefined
      : myCounsellorId;
    dispatch(fetchAppointments(buildAppointmentListParams(filters, page, perPage, cid)));
  }, [dispatch, page, perPage, filters, showAllAppointments, myCounsellorId]);

  useEffect(() => {
    const run = async () => {
      try {
        const response = await fetchWithAuth(`${url}/get_users?page=1&page_size=100`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (!response.ok) {
          setAllUsers([]);
          setCounsellors([]);
          return;
        }
        const data = await response.json();
        const users = Array.isArray(data) ? data : (data?.users ?? []);
        setAllUsers(users);
        const filtered = users.filter((item) => {
          const roles = parseUserRoles(item.role);
          return roles.some((r) => COUNSELLOR_ROLE_NAMES.includes(r));
        });
        filtered.sort((a, b) => String(a.username || "").localeCompare(String(b.username || "")));
        setCounsellors(filtered);
      } catch {
        setAllUsers([]);
        setCounsellors([]);
      }
    };
    run();
  }, []);

  const visibleRows = useMemo(() => {
    if (sortOrder === "asc") {
      return [...items].sort(
        (a, b) => new Date(a.start_time_utc || 0).getTime() - new Date(b.start_time_utc || 0).getTime()
      );
    }
    return items;
  }, [items, sortOrder]);

  const handleFilterChange = (name, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateAppointment = async (payload) => {
    setSubmitting(true);
    try {
      const data = await appointmentsApi.createAppointment(payload);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      setShowCreate(false);
      toast.success("Appointment created successfully.");
      const cid = showAllAppointments
        ? filters.counsellor_id
          ? Number(filters.counsellor_id)
          : undefined
        : myCounsellorId;
      dispatch(fetchAppointments(buildAppointmentListParams(filters, page, perPage, cid)));
    } finally {
      setSubmitting(false);
    }
  };

  const totalCount = pagination?.total ?? 0;
  const totalPages = Math.max(1, pagination?.pages ?? 1);
  const safePage = Math.min(page, totalPages);
  const rangeStart = totalCount === 0 ? 0 : (safePage - 1) * perPage + 1;
  const rangeEnd = Math.min(safePage * perPage, totalCount);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <ThemeProvider theme={theme}>
      <Box className={listStyles.root}>
        <Paper elevation={0} className={listStyles.contentPaper}>
          <Box sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="h4" className={listStyles.pageTitle}>
                  {pageTitle}
                </Typography>
                <Typography variant="body2" className={listStyles.pageSubtitle} sx={{ mt: 0.5 }}>
                  {pageSubtitle}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box className={listStyles.header}>
            <GlobalSearchBar
              value={filters.search}
              onChange={(v) => handleFilterChange("search", v)}
              placeholder="Search customer name or email…"
            />
            <Box className={listStyles.headerActions}>
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setFilterOpen(!filterOpen)}
                variant="outlined"
                size="small"
              >
                {filterOpen ? "Hide Filters" : "Show Filters"}
              </Button>
              <Tooltip title={viewMode === "cards" ? "Table View" : "Card View"}>
                <IconButton onClick={toggleViewMode} color="primary" size="small">
                  {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
                </IconButton>
              </Tooltip>
              <Button
                component={Link}
                to="/appointments/dashboard"
                variant="outlined"
                size="small"
                startIcon={<DashboardIcon />}
              >
                Meeting Dashboard
              </Button>
              <Button
                component={Link}
                to="/appointments/settings"
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
              >
                Settings
              </Button>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setShowCreate(true)} disabled={submitting}>
                Create Appointment
              </Button>
            </Box>
          </Box>

          {filterOpen && (
            <Fade in={filterOpen}>
              <FilterPaper variant="outlined">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="appt-status-label">Status</InputLabel>
                      <Select
                        labelId="appt-status-label"
                        label="Status"
                        value={filters.status}
                        onChange={(e) => handleFilterChange("status", e.target.value)}
                      >
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="booked">Booked</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                        <MenuItem value="attended">Attended</MenuItem>
                        <MenuItem value="missed">Missed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {showAllAppointments && (
                    <Grid item xs={12} sm={6} md={2.4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="appt-counsellor-label">Counsellor</InputLabel>
                        <Select
                          labelId="appt-counsellor-label"
                          label="Counsellor"
                          value={filters.counsellor_id}
                          onChange={(e) => handleFilterChange("counsellor_id", e.target.value)}
                        >
                          <MenuItem value="">All Counsellors</MenuItem>
                          {counsellors.map((item) => (
                            <MenuItem key={item.id} value={String(item.id)}>
                              {item.username}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={2.4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="appt-type-label">Type</InputLabel>
                      <Select
                        labelId="appt-type-label"
                        label="Type"
                        value={filters.appointment_type_id}
                        onChange={(e) => handleFilterChange("appointment_type_id", e.target.value)}
                      >
                        <MenuItem value="">All Types</MenuItem>
                        {types.map((item) => (
                          <MenuItem key={item.id} value={String(item.id)}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Customer email contains"
                      value={filters.customer_email}
                      onChange={(e) => handleFilterChange("customer_email", e.target.value)}
                      type="search"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Start date"
                      type="date"
                      value={filters.start_date}
                      onChange={(e) => handleFilterChange("start_date", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="End date"
                      type="date"
                      value={filters.end_date}
                      onChange={(e) => handleFilterChange("end_date", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2} className={listStyles.filterActions}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))}
                    >
                      Date: {sortOrder === "desc" ? "Newest first" : "Oldest first"}
                    </Button>
                  </Grid>
                </Grid>
              </FilterPaper>
            </Fade>
          )}

          {error && (
            <Typography color="error" align="center" sx={{ my: 2 }}>
              {error}
            </Typography>
          )}

          {loading && (
            <Box className={listStyles.loadingContainer}>
              {viewMode === "cards" ? (
                <Grid container spacing={2}>
                  {[...Array(6)].map((_, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
                </Box>
              )}
            </Box>
          )}

          {!loading && totalCount === 0 && (
            <Box className={listStyles.emptyState}>
              <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No appointments found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting filters or create a new appointment.
              </Typography>
            </Box>
          )}

          {!loading && totalCount > 0 && (
            <>
              <Box className={listStyles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing {rangeStart}–{rangeEnd} of {totalCount} appointments
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="appt-per-page-label">Per page</InputLabel>
                    <Select
                      labelId="appt-per-page-label"
                      value={perPage}
                      label="Per page"
                      onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={safePage}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>

              {visibleRows.length === 0 ? (
                <Box className={listStyles.emptyState}>
                  <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No rows on this page
                  </Typography>
                </Box>
              ) : viewMode === "cards" ? (
                <Grid container spacing={2} className={listStyles.cardsGrid}>
                  {visibleRows.map((row) => (
                    <Grid item xs={12} sm={6} md={4} key={row.id}>
                      <StyledCard variant="outlined" onClick={() => navigate(`/appointments/dashboard/${row.id}`)}>
                        <CardHeader
                          title={row.customer_name || "—"}
                          subheader={row.appointment_type?.name || "—"}
                          className={listStyles.cardHeader}
                          action={<StatusChip status={row.status} />}
                        />
                        <CardContent className={listStyles.cardContent}>
                          <Typography variant="body2" color="text.secondary">
                            {row.customer_email}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Start: {formatDateTime(row.start_time_utc, displayTimezone)}
                          </Typography>
                          <Typography variant="body2">End: {formatDateTime(row.end_time_utc, displayTimezone)}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Counsellor:{" "}
                            {row.counsellor_id
                              ? counsellorNameById.get(String(row.counsellor_id)) || `User #${row.counsellor_id}`
                              : "Unassigned"}
                          </Typography>
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper variant="outlined" className={listStyles.tableContainer}>
                  <Table stickyHeader size="small" className={listStyles.enquiriesTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>End</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Counsellor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleRows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          className={listStyles.tableRow}
                          onClick={() => navigate(`/appointments/dashboard/${row.id}`)}
                        >
                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.appointment_type?.name || "—"}</TableCell>
                          <TableCell>
                            <Box className={listStyles.nameCell}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" className={listStyles.primaryText} noWrap>
                                  {row.customer_name || "—"}
                                </Typography>
                                <Typography variant="caption" className={listStyles.secondaryText} noWrap>
                                  {row.customer_email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {formatDateTime(row.start_time_utc, displayTimezone)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {formatDateTime(row.end_time_utc, displayTimezone)}
                            </Typography>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <StatusChip status={row.status} />
                          </TableCell>
                          <TableCell>
                            {row.counsellor_id
                              ? counsellorNameById.get(String(row.counsellor_id)) || `User #${row.counsellor_id}`
                              : "Unassigned"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </>
          )}
        </Paper>

        <CreateAppointmentWizard
          show={showCreate}
          onHide={() => setShowCreate(false)}
          username={username}
          types={types}
          onSubmit={handleCreateAppointment}
          isSubmitting={submitting}
        />
      </Box>
    </ThemeProvider>
  );
}
